import React, { useMemo } from 'react'
import apiService from 'services/api'
import LoadingScreen from 'components/Layout/LoadingScreen'
import {
  CompanyPageLayout,
  CompanyContentSections,
} from 'components/NewCompanyPage'
import { UnmatchedDoorsModal } from 'components/NewCompanyPage/Modals/UnmatchedDoorsModal'
import {
  MetricCard,
  StatsCard,
  Divider,
  DemographicsSection,
  DescriptionAndContactInfo,
} from 'components/NewCompanyPage/Sidebar/shared'
import { formatInteger, formatAddress } from 'utils/formatting'
import useDocumentTitle from 'components/useDocumentTitle'
import { useHideFooter } from 'context/layout'

export function NewK12DistrictPage({
  k12DistrictID,
}: {
  k12DistrictID?: number
}) {
  const api = apiService()
  const { data, isLoading } = api.useGetK12District(k12DistrictID)

  // Hide footer on company pages (must be called before loading check)
  useHideFooter()

  // Set document title
  useDocumentTitle(
    `${data?.k12district?.lea_name || 'Not Found'} (K12 District) - First Bite`
  )

  const [showDoorsModal, setShowDoorsModal] = React.useState(false)

  const entityBreadcrumbs = useMemo(
    () =>
      data?.k12district?.lea_name
        ? [{ title: 'Company', label: data.k12district.lea_name }]
        : [],
    [data?.k12district?.lea_name]
  )

  if (isLoading) {
    return <LoadingScreen />
  }

  // Build stats for the summary tab
  const operationalStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Total Students',
      value: data?.k12district?.student_count
        ? formatInteger(data.k12district.student_count)
        : 'N/A',
    },
    {
      label: 'Total Teachers',
      value: data?.k12district?.teachers_count
        ? formatInteger(data.k12district.teachers_count)
        : 'N/A',
    },
    {
      label: 'Operational Schools',
      value: data?.k12district?.operational_schools
        ? formatInteger(data.k12district.operational_schools)
        : 'N/A',
    },
    {
      label: 'County',
      value: data?.k12district?.county || 'N/A',
    },
    {
      label: 'Locale Type',
      value: data?.k12district?.locale || 'N/A',
    },
    {
      label: 'Status',
      value: data?.k12district?.sy_status_text || 'N/A',
    },
  ]

  // Financial and lunch program stats
  const financialStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Total Revenue',
      value: data?.k12district?.revenue
        ? `$${formatInteger(data.k12district.revenue)}`
        : 'N/A',
    },
    {
      label: 'Food Service Expenditures',
      value: data?.k12district?.food_service_expenditures
        ? `$${formatInteger(data.k12district.food_service_expenditures)}`
        : 'N/A',
    },
    {
      label: 'Estimated Meals/Day',
      value: data?.k12district?.estimated_meals_per_day
        ? formatInteger(data.k12district.estimated_meals_per_day)
        : 'N/A',
    },
  ]

  // Lunch program stats
  const lunchProgramStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Free Lunch Students',
      value: data?.k12district?.free_lunch
        ? `${formatInteger(data.k12district.free_lunch)} (${data.k12district.free_lunch_perc?.toFixed(1) || '0'}%)`
        : 'N/A',
    },
    {
      label: 'Reduced Lunch Students',
      value: data?.k12district?.reduced_lunch
        ? `${formatInteger(data.k12district.reduced_lunch)} (${data.k12district.reduced_lunch_perc?.toFixed(1) || '0'}%)`
        : 'N/A',
    },
    {
      label: 'Total Supplemented Lunch',
      value: data?.k12district?.total_supplemented_lunch
        ? `${formatInteger(data.k12district.total_supplemented_lunch)} (${data.k12district.total_supplemented_lunch_perc?.toFixed(1) || '0'}%)`
        : 'N/A',
    },
  ]

  // Build summary tab content
  const summaryTabContent: React.ReactNode[] = []

  // Description and Contact Info
  summaryTabContent.push(
    <DescriptionAndContactInfo
      key="description-contact"
      phone={data?.k12district?.phone ?? undefined}
      googlePlaceUrl={data?.k12district?.google_place_url}
      address={
        formatAddress({
          street: data?.k12district?.address,
          city: data?.k12district?.lcity,
          state: data?.k12district?.lstate,
          zip: data?.k12district?.zipcode,
        }) ?? undefined
      }
    />
  )

  if (data?.doors_count && data?.doors_count > 0) {
    summaryTabContent.push(
      <MetricCard
        key="doors"
        value={data?.doors_count}
        label="Door Count"
        actionText="View Doors"
        onActionClick={() => setShowDoorsModal(true)}
      />
    )
  }

  // Operational Stats Section
  summaryTabContent.push(
    <StatsCard key="operational-stats" stats={operationalStats} />
  )
  summaryTabContent.push(<Divider key="divider-1" />)

  // Financial Stats Section
  summaryTabContent.push(
    <StatsCard key="financial-stats" stats={financialStats} />
  )
  summaryTabContent.push(<Divider key="divider-2" />)

  // Lunch Program Stats Section
  summaryTabContent.push(
    <StatsCard key="lunch-program-stats" stats={lunchProgramStats} />
  )

  // Build location details tab content
  const locationDetailsTabContent: React.ReactNode[] = []

  // District Location - similar to HQ City for chains and Campus Location for universities
  const districtLocation = data?.k12district?.address
    ? `${data.k12district.lcity}, ${data.k12district.lstate} - USA`
    : 'N/A'

  locationDetailsTabContent.push(
    <MetricCard
      key="district-location"
      value={districtLocation}
      label="District Location"
      variant="full-width"
    />
  )

  // Location Details Stats
  const locationInfoStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Full Address',
      value: data?.k12district?.address || 'N/A',
    },
    {
      label: 'ZIP Code',
      value: data?.k12district?.zipcode || 'N/A',
    },
    {
      label: 'State',
      value: data?.k12district?.lstate || 'N/A',
    },
    {
      label: 'City',
      value: data?.k12district?.lcity || 'N/A',
    },
    {
      label: 'Phone',
      value: data?.k12district?.phone || 'N/A',
    },
    {
      label: 'School Year',
      value: data?.k12district?.school_year || 'N/A',
    },
  ]

  locationDetailsTabContent.push(
    <StatsCard key="location-info" stats={locationInfoStats} />
  )

  if (data?.k12district?.address_info) {
    locationDetailsTabContent.push(
      <Divider key="divider-2" />,
      <DemographicsSection
        key="zipcode-demographics"
        addressInfo={data?.k12district?.address_info}
      />
    )
  }

  return (
    <>
      <CompanyPageLayout
        chainProxyId={data!.id!}
        breadcrumbRoot="Education - K12 District"
        breadcrumbs={[
          {
            title: 'K12 District',
            breadcrumb: data!.k12district!.lea_name!,
            url: undefined,
          },
        ]}
        entityBreadcrumbs={entityBreadcrumbs}
        categoryInfo={{
          categorySlug: 'non-commercial',
          subCategorySlug: 'education-k-12',
        }}
        sidebar={{
          header: {
            name: data!.k12district!.lea_name!,
            domain: data!.k12district!.website!,
          },
          tabs: [
            {
              key: 'summary',
              title: 'Summary',
              content: summaryTabContent,
            },
            {
              key: 'location-details',
              title: 'Location Details',
              content: locationDetailsTabContent,
            },
          ],
        }}
      >
        <CompanyContentSections
          chainProxyId={data!.id!}
          entityID={k12DistrictID}
          companyName={data!.k12district!.lea_name!}
          websiteAddress={data!.k12district!.website!}
          companyTypeSlug="education-k-12"
          tableTypeSlug="education-k-12"
        />
      </CompanyPageLayout>

      {/* Unmatched Doors Modal */}
      {data && (
        <UnmatchedDoorsModal
          show={showDoorsModal}
          onHide={() => setShowDoorsModal(false)}
          forChainProxyId={data.id}
          chainData={{
            id: data.id,
            name: data.k12district?.lea_name || '',
            domain: data.k12district?.website,
          }}
          subCategory={{
            name: 'K-12 Education',
            slug: 'education-k-12',
            description: 'K-12 Education',
          }}
          companyName={data.k12district?.lea_name}
        />
      )}
    </>
  )
}
